import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { useHistorique } from '../lib/historique'
import { Superpose } from '../components/Superpose'
import { jourCalendrier } from '../lib/format'
import {
  CATEGORIES,
  TYPES_ACTIVITE,
  aujourdhui,
  cleCompagnie,
  correspond,
  libelleCategorie,
  libelleType,
  lienTel,
  lireCarnet,
  vide,
  type Activite,
  type Carnet,
  type Categorie,
  type Compagnie,
  type ContactCrm,
  type TypeActivite
} from '../lib/crm'

/**
 * Module Ventes, lot 1 : carnet de compagnies et de contacts, journal des
 * échanges et relances. Réservé aux admins et au superadmin (la base refuse
 * tout aux autres rôles : migration 016). En ligne seulement.
 */
type Vue = 'compagnies' | 'contacts' | 'relances'
type Ouvert =
  | { type: 'compagnie'; id: string | null }
  | { type: 'contact'; id: string | null; compagnieId?: string | null }
  | null

export function Ventes({ fermer }: { fermer: () => void }) {
  const [carnet, setCarnet] = useState<Carnet | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [vue, setVue] = useState<Vue>('compagnies')
  const [requete, setRequete] = useState('')
  const [ouvert, setOuvert] = useState<Ouvert>(null)
  useHistorique(ouvert, setOuvert, (o) => (o ? `${o.type}:${o.id ?? 'nouveau'}` : null))

  const rafraichir = useCallback(async () => {
    try {
      setCarnet(await lireCarnet())
      setErreur(null)
    } catch (e) {
      const m = (e as Error).message
      setErreur(/Failed to fetch|NetworkError/i.test(m) ? 'Pas de réseau : le module Ventes fonctionne en ligne seulement.' : m)
    }
  }, [])

  useEffect(() => {
    void rafraichir()
  }, [rafraichir])

  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if (e.key === 'Escape' && !ouvert) fermer()
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [fermer, ouvert])

  const nomCompagnie = useMemo(() => {
    const m = new Map<string, string>()
    carnet?.compagnies.forEach((c) => m.set(c.id, c.nom))
    return m
  }, [carnet])

  const nbContacts = useMemo(() => {
    const m = new Map<string, number>()
    carnet?.contacts.forEach((c) => c.compagnie_id && m.set(c.compagnie_id, (m.get(c.compagnie_id) ?? 0) + 1))
    return m
  }, [carnet])

  const relances = useMemo(
    () =>
      (carnet?.activites ?? [])
        .filter((a) => a.relance_le && !a.relance_faite)
        .sort((a, b) => a.relance_le!.localeCompare(b.relance_le!)),
    [carnet]
  )
  const enRetard = relances.filter((a) => a.relance_le! <= aujourdhui()).length

  const compagnies = (carnet?.compagnies ?? []).filter((c) =>
    correspond(requete, c.nom, c.courriel, c.telephone, c.adresse, c.notes, libelleCategorie(c.categorie))
  )
  const contacts = (carnet?.contacts ?? []).filter((c) =>
    correspond(requete, c.nom, c.fonction, c.courriel, c.telephone, c.cellulaire, c.compagnie_id ? nomCompagnie.get(c.compagnie_id) : null)
  )

  return (
    <Superpose className="feuille">
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Chantiers
          </button>
          <div style={{ flex: 1 }}>
            <h1>Ventes</h1>
            <p className="sous">Compagnies, contacts, relances · admins</p>
          </div>
          <button className="fermer-x" onClick={fermer} aria-label="Fermer">
            ✕
          </button>
        </div>
      </header>
      <div className="sous-onglets" role="tablist">
        <button role="tab" aria-selected={vue === 'compagnies'} onClick={() => setVue('compagnies')}>
          Compagnies <span className="compte">{carnet?.compagnies.length ?? ''}</span>
        </button>
        <button role="tab" aria-selected={vue === 'contacts'} onClick={() => setVue('contacts')}>
          Contacts <span className="compte">{carnet?.contacts.length ?? ''}</span>
        </button>
        <button role="tab" aria-selected={vue === 'relances'} onClick={() => setVue('relances')}>
          Relances
          {relances.length > 0 && <span className={`compte ${enRetard ? 'alerte' : ''}`}>{relances.length}</span>}
        </button>
      </div>
      <div className="corps">
        {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}
        {carnet === null && !erreur && <p className="vide">Chargement…</p>}

        {carnet && vue !== 'relances' && (
          <>
            <label className="champ">
              <input
                type="search"
                value={requete}
                onChange={(e) => setRequete(e.target.value)}
                placeholder={vue === 'compagnies' ? 'Chercher une compagnie…' : 'Chercher un nom, une compagnie, un numéro…'}
              />
            </label>
            <button
              className="bouton"
              style={{ marginBottom: 12 }}
              onClick={() => setOuvert(vue === 'compagnies' ? { type: 'compagnie', id: null } : { type: 'contact', id: null })}
            >
              {vue === 'compagnies' ? '+ Nouvelle compagnie' : '+ Nouveau contact'}
            </button>
          </>
        )}

        {carnet && vue === 'compagnies' && (
          <div className="liste">
            {compagnies.map((c) => (
              <button key={c.id} className="carte" onClick={() => setOuvert({ type: 'compagnie', id: c.id })}>
                <div className="entre">
                  <p className="titre">{c.nom}</p>
                  <span className="code">{nbContacts.get(c.id) ?? 0} contact{(nbContacts.get(c.id) ?? 0) > 1 ? 's' : ''}</span>
                </div>
                {(c.categorie || c.telephone) && (
                  <div className="jetons">
                    {c.categorie && <span className="jeton">{libelleCategorie(c.categorie)}</span>}
                    {c.telephone && <span className="jeton">{c.telephone}</span>}
                  </div>
                )}
              </button>
            ))}
            {compagnies.length === 0 && <p className="vide">Aucune compagnie ne correspond.</p>}
          </div>
        )}

        {carnet && vue === 'contacts' && (
          <div className="liste">
            {contacts.map((c) => (
              <button key={c.id} className="carte" onClick={() => setOuvert({ type: 'contact', id: c.id })}>
                <p className="titre">{c.nom}</p>
                <p className="meta">
                  <span>{[c.fonction, c.compagnie_id ? nomCompagnie.get(c.compagnie_id) : null].filter(Boolean).join(' · ')}</span>
                </p>
                {(c.cellulaire || c.telephone || c.courriel) && (
                  <div className="jetons">
                    {(c.cellulaire || c.telephone) && <span className="jeton">{c.cellulaire || c.telephone}</span>}
                    {c.courriel && <span className="jeton">{c.courriel}</span>}
                  </div>
                )}
              </button>
            ))}
            {contacts.length === 0 && <p className="vide">Aucun contact ne correspond.</p>}
          </div>
        )}

        {carnet && vue === 'relances' && (
          <>
            <p className="code" style={{ marginBottom: 10 }}>
              Les échanges notés avec une date de relance. En gras : à faire aujourd'hui ou en retard.
            </p>
            <div className="liste">
              {relances.map((a) => (
                <button
                  key={a.id}
                  className="carte"
                  onClick={() =>
                    setOuvert(a.compagnie_id ? { type: 'compagnie', id: a.compagnie_id } : { type: 'contact', id: a.contact_id })
                  }
                >
                  <div className="entre">
                    <p className="titre" style={{ fontWeight: a.relance_le! <= aujourdhui() ? 800 : 600 }}>
                      {jourCalendrier(a.relance_le!)}
                    </p>
                    {a.relance_le! < aujourdhui() && <span className="pastille p-alerte">En retard</span>}
                  </div>
                  <p className="meta">
                    <span>
                      {[a.compagnie_id ? nomCompagnie.get(a.compagnie_id) : null, carnet.contacts.find((x) => x.id === a.contact_id)?.nom]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </p>
                  <p className="meta">
                    <span>{a.objet}</span>
                  </p>
                </button>
              ))}
              {relances.length === 0 && <p className="vide">Aucune relance à faire.</p>}
            </div>
          </>
        )}
      </div>

      {carnet && ouvert?.type === 'compagnie' && (
        <FicheCompagnie
          key={ouvert.id ?? 'nouvelle'}
          compagnie={carnet.compagnies.find((c) => c.id === ouvert.id) ?? null}
          carnet={carnet}
          rafraichir={rafraichir}
          fermer={() => setOuvert(null)}
          ouvrirContact={(id, compagnieId) => setOuvert({ type: 'contact', id, compagnieId })}
          creee={(id) => setOuvert({ type: 'compagnie', id })}
        />
      )}
      {carnet && ouvert?.type === 'contact' && (
        <FicheContact
          key={ouvert.id ?? 'nouveau'}
          contact={carnet.contacts.find((c) => c.id === ouvert.id) ?? null}
          compagnieInitiale={ouvert.compagnieId ?? null}
          carnet={carnet}
          rafraichir={rafraichir}
          fermer={() => setOuvert(null)}
          ouvrirCompagnie={(id) => setOuvert({ type: 'compagnie', id })}
          cree={(id) => setOuvert({ type: 'contact', id })}
        />
      )}
    </Superpose>
  )
}

/* ------------------------------------------------------------------ */

function Entete({ titre, sous, fermer }: { titre: string; sous?: string; fermer: () => void }) {
  return (
    <header className="entete">
      <div className="entete-rang">
        <button className="retour" onClick={fermer}>
          ‹ Ventes
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{titre}</h1>
          {sous && <p className="sous">{sous}</p>}
        </div>
      </div>
    </header>
  )
}

function Coordonnees({ tel, cell, courriel }: { tel?: string | null; cell?: string | null; courriel?: string | null }) {
  if (!tel && !cell && !courriel) return null
  return (
    <p className="meta" style={{ gap: 14, flexWrap: 'wrap' }}>
      {cell && (
        <a className="lien-contact" href={lienTel(cell)}>
          📱 {cell}
        </a>
      )}
      {tel && (
        <a className="lien-contact" href={lienTel(tel)}>
          ☎ {tel}
        </a>
      )}
      {courriel && (
        <a className="lien-contact" href={`mailto:${courriel}`}>
          ✉ {courriel}
        </a>
      )}
    </p>
  )
}

/* --- Compagnie ------------------------------------------------------ */

function FicheCompagnie({
  compagnie,
  carnet,
  rafraichir,
  fermer,
  ouvrirContact,
  creee
}: {
  compagnie: Compagnie | null
  carnet: Carnet
  rafraichir: () => Promise<void>
  fermer: () => void
  ouvrirContact: (id: string | null, compagnieId: string) => void
  creee: (id: string) => void
}) {
  const [edition, setEdition] = useState(compagnie === null)
  const [f, setF] = useState({
    nom: compagnie?.nom ?? '',
    categorie: (compagnie?.categorie ?? '') as Categorie | '',
    telephone: compagnie?.telephone ?? '',
    courriel: compagnie?.courriel ?? '',
    adresse: compagnie?.adresse ?? '',
    site_web: compagnie?.site_web ?? '',
    notes: compagnie?.notes ?? ''
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  const semblables = useMemo(() => {
    const k = cleCompagnie(f.nom)
    if (!edition || k.length < 3) return []
    return carnet.compagnies.filter((c) => c.id !== compagnie?.id && cleCompagnie(c.nom) === k)
  }, [f.nom, edition, carnet, compagnie])

  async function enregistrer(e: FormEvent) {
    e.preventDefault()
    if (!f.nom.trim()) return setErreur('Le nom est obligatoire.')
    setOccupe(true)
    setErreur(null)
    const valeurs = {
      nom: f.nom.trim(),
      categorie: f.categorie || null,
      telephone: vide(f.telephone),
      courriel: vide(f.courriel)?.toLowerCase() ?? null,
      adresse: vide(f.adresse),
      site_web: vide(f.site_web),
      notes: vide(f.notes)
    }
    const r = compagnie
      ? await supabase.from('compagnies').update(valeurs).eq('id', compagnie.id).select('id').single()
      : await supabase.from('compagnies').insert(valeurs).select('id').single()
    setOccupe(false)
    if (r.error) return setErreur(r.error.message)
    await rafraichir()
    setEdition(false)
    if (!compagnie) creee(r.data.id)
  }

  async function supprimer() {
    if (!compagnie) return
    const n = carnet.contacts.filter((c) => c.compagnie_id === compagnie.id).length
    if (
      !window.confirm(
        `Retirer « ${compagnie.nom} » du carnet ?` + (n ? `\n\nSes ${n} contact(s) restent dans le carnet, sans compagnie.` : '')
      )
    )
      return
    setOccupe(true)
    await supabase.from('contacts').update({ compagnie_id: null }).eq('compagnie_id', compagnie.id)
    const { error } = await supabase.from('compagnies').update({ supprime_le: new Date().toISOString() }).eq('id', compagnie.id)
    setOccupe(false)
    if (error) return setErreur(error.message)
    await rafraichir()
    fermer()
  }

  const contacts = compagnie ? carnet.contacts.filter((c) => c.compagnie_id === compagnie.id) : []

  return (
    <Superpose className="feuille">
      <Entete
        titre={compagnie?.nom ?? 'Nouvelle compagnie'}
        sous={compagnie ? libelleCategorie(compagnie.categorie) ?? 'Compagnie' : undefined}
        fermer={fermer}
      />
      <div className="corps">
        {edition ? (
          <form className="carte bande-nulle" onSubmit={(e) => void enregistrer(e)}>
            <label className="champ">
              <span>Nom *</span>
              <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} autoFocus={!compagnie} />
            </label>
            {semblables.length > 0 && (
              <p className="avis" style={{ marginBottom: 12 }}>
                Déjà au carnet : {semblables.map((c) => c.nom).join(', ')}. Vérifie qu'il ne s'agit pas de la même compagnie.
              </p>
            )}
            <label className="champ">
              <span>Catégorie</span>
              <select value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value as Categorie | '' })}>
                <option value="">— Non classée —</option>
                {CATEGORIES.map((c) => (
                  <option key={c.cle} value={c.cle}>
                    {c.libelle}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ">
              <span>Téléphone</span>
              <input type="tel" inputMode="tel" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} />
            </label>
            <label className="champ">
              <span>Courriel général</span>
              <input type="email" inputMode="email" value={f.courriel} onChange={(e) => setF({ ...f, courriel: e.target.value })} />
            </label>
            <label className="champ">
              <span>Adresse</span>
              <input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} />
            </label>
            <label className="champ">
              <span>Site web</span>
              <input inputMode="url" value={f.site_web} onChange={(e) => setF({ ...f, site_web: e.target.value })} />
            </label>
            <label className="champ">
              <span>Notes</span>
              <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
            </label>
            {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="bouton creux"
                disabled={occupe}
                onClick={() => (compagnie ? setEdition(false) : fermer())}
              >
                Annuler
              </button>
              <button className="bouton" disabled={occupe}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
            {compagnie && (
              <button
                type="button"
                className="retour"
                style={{ marginTop: 12, textDecoration: 'underline' }}
                disabled={occupe}
                onClick={() => void supprimer()}
              >
                Retirer cette compagnie du carnet
              </button>
            )}
          </form>
        ) : (
          compagnie && (
            <div className="carte bande-nulle">
              <div className="entre">
                <div style={{ minWidth: 0 }}>
                  <Coordonnees tel={compagnie.telephone} courriel={compagnie.courriel} />
                  {compagnie.adresse && (
                    <p className="meta">
                      <span>{compagnie.adresse}</span>
                    </p>
                  )}
                  {compagnie.site_web && (
                    <p className="meta">
                      <a
                        className="lien-contact"
                        href={/^https?:/i.test(compagnie.site_web) ? compagnie.site_web : `https://${compagnie.site_web}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {compagnie.site_web.replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                      </a>
                    </p>
                  )}
                  {compagnie.notes && <p style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{compagnie.notes}</p>}
                  {!compagnie.telephone && !compagnie.courriel && !compagnie.adresse && !compagnie.site_web && !compagnie.notes && (
                    <p className="code">Aucune coordonnée.</p>
                  )}
                </div>
                <button className="pastille p-ouvert" onClick={() => setEdition(true)}>
                  Modifier
                </button>
              </div>
            </div>
          )
        )}

        {compagnie && (
          <>
            <div className="sect">
              <span>
                Contacts <span className="code">{contacts.length}</span>
              </span>
              <button className="retour" onClick={() => ouvrirContact(null, compagnie.id)}>
                + Ajouter
              </button>
            </div>
            <div className="liste">
              {contacts.map((c) => (
                <div key={c.id} className="carte bande-nulle" onClick={() => ouvrirContact(c.id, compagnie.id)} style={{ cursor: 'pointer' }}>
                  <p className="titre">{c.nom}</p>
                  {c.fonction && (
                    <p className="meta">
                      <span>{c.fonction}</span>
                    </p>
                  )}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Coordonnees tel={c.telephone} cell={c.cellulaire} courriel={c.courriel} />
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <p className="vide">Aucun contact pour cette compagnie.</p>}
            </div>
            <Journal compagnieId={compagnie.id} contactId={null} carnet={carnet} rafraichir={rafraichir} />
          </>
        )}
      </div>
    </Superpose>
  )
}

/* --- Contact -------------------------------------------------------- */

function FicheContact({
  contact,
  compagnieInitiale,
  carnet,
  rafraichir,
  fermer,
  ouvrirCompagnie,
  cree
}: {
  contact: ContactCrm | null
  compagnieInitiale: string | null
  carnet: Carnet
  rafraichir: () => Promise<void>
  fermer: () => void
  ouvrirCompagnie: (id: string) => void
  cree: (id: string) => void
}) {
  const [edition, setEdition] = useState(contact === null)
  const [f, setF] = useState({
    nom: contact?.nom ?? '',
    compagnie_id: contact?.compagnie_id ?? compagnieInitiale ?? '',
    fonction: contact?.fonction ?? '',
    cellulaire: contact?.cellulaire ?? '',
    telephone: contact?.telephone ?? '',
    courriel: contact?.courriel ?? '',
    notes: contact?.notes ?? ''
  })
  const [nouvelleCompagnie, setNouvelleCompagnie] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [chantiers, setChantiers] = useState<{ id: string; numero: string; nom: string }[]>([])

  useEffect(() => {
    if (!contact) return
    void supabase
      .from('chantier_contacts')
      .select('chantier:chantiers(id, numero, nom)')
      .eq('contact_id', contact.id)
      .then(({ data }) => {
        const l = ((data ?? []) as unknown as { chantier: { id: string; numero: string; nom: string } | null }[])
          .map((x) => x.chantier)
          .filter((x): x is { id: string; numero: string; nom: string } => !!x)
        setChantiers(l.filter((x, i) => l.findIndex((y) => y.id === x.id) === i))
      })
  }, [contact])

  const doublon = useMemo(() => {
    const c = f.courriel.trim().toLowerCase()
    if (!edition || !c) return null
    return carnet.contacts.find((x) => x.id !== contact?.id && x.courriel?.toLowerCase() === c) ?? null
  }, [f.courriel, edition, carnet, contact])

  async function enregistrer(e: FormEvent) {
    e.preventDefault()
    if (!f.nom.trim()) return setErreur('Le nom est obligatoire.')
    setOccupe(true)
    setErreur(null)
    let compagnieId: string | null = f.compagnie_id || null
    if (f.compagnie_id === '__nouvelle__') {
      if (!nouvelleCompagnie.trim()) {
        setOccupe(false)
        return setErreur('Nom de la nouvelle compagnie ?')
      }
      const r = await supabase.from('compagnies').insert({ nom: nouvelleCompagnie.trim() }).select('id').single()
      if (r.error) {
        setOccupe(false)
        return setErreur(r.error.message)
      }
      compagnieId = r.data.id
    }
    const valeurs = {
      nom: f.nom.trim(),
      compagnie_id: compagnieId,
      fonction: vide(f.fonction),
      cellulaire: vide(f.cellulaire),
      telephone: vide(f.telephone),
      courriel: vide(f.courriel)?.toLowerCase() ?? null,
      notes: vide(f.notes)
    }
    const r = contact
      ? await supabase.from('contacts').update(valeurs).eq('id', contact.id).select('id').single()
      : await supabase.from('contacts').insert(valeurs).select('id').single()
    setOccupe(false)
    if (r.error) return setErreur(r.error.message)
    await rafraichir()
    setEdition(false)
    setF((x) => ({ ...x, compagnie_id: compagnieId ?? '' }))
    if (!contact) cree(r.data.id)
  }

  async function supprimer() {
    if (!contact) return
    if (
      !window.confirm(
        `Retirer ${contact.nom} du carnet ?` +
          (chantiers.length ? `\n\nIl reste affiché dans les contacts de ${chantiers.length} chantier(s), sans lien au carnet.` : '')
      )
    )
      return
    setOccupe(true)
    const { error } = await supabase.from('contacts').update({ supprime_le: new Date().toISOString() }).eq('id', contact.id)
    if (!error) await supabase.from('chantier_contacts').update({ contact_id: null }).eq('contact_id', contact.id)
    setOccupe(false)
    if (error) return setErreur(error.message)
    await rafraichir()
    fermer()
  }

  const compagnie = carnet.compagnies.find((c) => c.id === contact?.compagnie_id) ?? null

  return (
    <Superpose className="feuille">
      <Entete titre={contact?.nom ?? 'Nouveau contact'} sous={[contact?.fonction, compagnie?.nom].filter(Boolean).join(' · ') || undefined} fermer={fermer} />
      <div className="corps">
        {edition ? (
          <form className="carte bande-nulle" onSubmit={(e) => void enregistrer(e)}>
            <label className="champ">
              <span>Nom *</span>
              <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} autoFocus={!contact} />
            </label>
            <label className="champ">
              <span>Compagnie</span>
              <select value={f.compagnie_id} onChange={(e) => setF({ ...f, compagnie_id: e.target.value })}>
                <option value="">— Aucune —</option>
                <option value="__nouvelle__">+ Nouvelle compagnie…</option>
                {carnet.compagnies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
            </label>
            {f.compagnie_id === '__nouvelle__' && (
              <label className="champ">
                <span>Nom de la nouvelle compagnie</span>
                <input value={nouvelleCompagnie} onChange={(e) => setNouvelleCompagnie(e.target.value)} />
              </label>
            )}
            <label className="champ">
              <span>Fonction</span>
              <input
                value={f.fonction}
                onChange={(e) => setF({ ...f, fonction: e.target.value })}
                placeholder="Ex. : chargé de projet, surintendant, estimateur"
              />
            </label>
            <label className="champ">
              <span>Cellulaire</span>
              <input type="tel" inputMode="tel" value={f.cellulaire} onChange={(e) => setF({ ...f, cellulaire: e.target.value })} />
            </label>
            <label className="champ">
              <span>Téléphone (bureau)</span>
              <input type="tel" inputMode="tel" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} />
            </label>
            <label className="champ">
              <span>Courriel</span>
              <input type="email" inputMode="email" value={f.courriel} onChange={(e) => setF({ ...f, courriel: e.target.value })} />
            </label>
            {doublon && (
              <p className="avis" style={{ marginBottom: 12 }}>
                Ce courriel est déjà celui de {doublon.nom}. Vérifie qu'il ne s'agit pas de la même personne.
              </p>
            )}
            <label className="champ">
              <span>Notes</span>
              <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
            </label>
            {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="bouton creux" disabled={occupe} onClick={() => (contact ? setEdition(false) : fermer())}>
                Annuler
              </button>
              <button className="bouton" disabled={occupe}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
            {contact && (
              <button
                type="button"
                className="retour"
                style={{ marginTop: 12, textDecoration: 'underline' }}
                disabled={occupe}
                onClick={() => void supprimer()}
              >
                Retirer ce contact du carnet
              </button>
            )}
          </form>
        ) : (
          contact && (
            <div className="carte bande-nulle">
              <div className="entre">
                <div style={{ minWidth: 0 }}>
                  {compagnie && (
                    <p className="meta">
                      <button className="lien-contact" style={{ padding: 0 }} onClick={() => ouvrirCompagnie(compagnie.id)}>
                        {compagnie.nom} ›
                      </button>
                    </p>
                  )}
                  <Coordonnees tel={contact.telephone} cell={contact.cellulaire} courriel={contact.courriel} />
                  {contact.notes && <p style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{contact.notes}</p>}
                </div>
                <button className="pastille p-ouvert" onClick={() => setEdition(true)}>
                  Modifier
                </button>
              </div>
            </div>
          )
        )}

        {contact && chantiers.length > 0 && (
          <>
            <div className="sect">Chantiers</div>
            <div className="jetons">
              {chantiers.map((c) => (
                <span key={c.id} className="jeton">
                  {c.numero} · {c.nom}
                </span>
              ))}
            </div>
          </>
        )}

        {contact && (
          <Journal compagnieId={contact.compagnie_id} contactId={contact.id} carnet={carnet} rafraichir={rafraichir} />
        )}
      </div>
    </Superpose>
  )
}

/* --- Journal des échanges ------------------------------------------ */

function Journal({
  compagnieId,
  contactId,
  carnet,
  rafraichir
}: {
  compagnieId: string | null
  contactId: string | null
  carnet: Carnet
  rafraichir: () => Promise<void>
}) {
  const [ajout, setAjout] = useState(false)
  const vierge = { quand: aujourdhui(), type: 'telephone' as TypeActivite, contact_id: contactId ?? '', objet: '', notes: '', relance_le: '' }
  const [f, setF] = useState(vierge)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  // Fiche compagnie : tous ses échanges. Fiche contact : les siens.
  const lignes: Activite[] = carnet.activites.filter((a) => (contactId ? a.contact_id === contactId : a.compagnie_id === compagnieId))
  const contactsCompagnie = compagnieId ? carnet.contacts.filter((c) => c.compagnie_id === compagnieId) : []

  async function ajouter(e: FormEvent) {
    e.preventDefault()
    if (!f.objet.trim()) return setErreur('Résume l’échange en une ligne.')
    setOccupe(true)
    setErreur(null)
    const { error } = await supabase.from('activites').insert({
      compagnie_id: compagnieId,
      contact_id: f.contact_id || null,
      quand: f.quand || aujourdhui(),
      type: f.type,
      objet: f.objet.trim(),
      notes: vide(f.notes),
      relance_le: f.relance_le || null
    })
    setOccupe(false)
    if (error) return setErreur(error.message)
    setF(vierge)
    setAjout(false)
    await rafraichir()
  }

  async function basculer(a: Activite) {
    await supabase.from('activites').update({ relance_faite: !a.relance_faite }).eq('id', a.id)
    await rafraichir()
  }

  async function retirer(a: Activite) {
    if (!window.confirm(`Retirer « ${a.objet} » du journal ?`)) return
    await supabase.from('activites').update({ supprime_le: new Date().toISOString() }).eq('id', a.id)
    await rafraichir()
  }

  return (
    <>
      <div className="sect">
        <span>
          Journal des échanges <span className="code">{lignes.length}</span>
        </span>
        {!ajout && (
          <button className="retour" onClick={() => setAjout(true)}>
            + Noter
          </button>
        )}
      </div>
      {ajout && (
        <form className="carte bande-nulle" style={{ marginBottom: 10 }} onSubmit={(e) => void ajouter(e)}>
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="champ" style={{ flex: 1 }}>
              <span>Date</span>
              <input type="date" value={f.quand} onChange={(e) => setF({ ...f, quand: e.target.value })} />
            </label>
            <label className="champ" style={{ flex: 1 }}>
              <span>Type</span>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as TypeActivite })}>
                {TYPES_ACTIVITE.map((t) => (
                  <option key={t.cle} value={t.cle}>
                    {t.libelle}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {!contactId && contactsCompagnie.length > 0 && (
            <label className="champ">
              <span>Avec</span>
              <select value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })}>
                <option value="">— La compagnie —</option>
                {contactsCompagnie.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="champ">
            <span>Objet *</span>
            <input value={f.objet} onChange={(e) => setF({ ...f, objet: e.target.value })} placeholder="Ex. : appel pour le projet Remembrance" autoFocus />
          </label>
          <label className="champ">
            <span>Notes</span>
            <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </label>
          <label className="champ">
            <span>Relancer le (facultatif)</span>
            <input type="date" value={f.relance_le} onChange={(e) => setF({ ...f, relance_le: e.target.value })} />
          </label>
          {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="bouton creux" disabled={occupe} onClick={() => setAjout(false)}>
              Annuler
            </button>
            <button className="bouton" disabled={occupe}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}
      <div className="liste">
        {lignes.map((a) => {
          const avec = a.contact_id && !contactId ? carnet.contacts.find((c) => c.id === a.contact_id)?.nom : null
          return (
            <div key={a.id} className="carte bande-nulle">
              <p className="code">
                {jourCalendrier(a.quand)} · {libelleType(a.type)}
                {avec ? ` · ${avec}` : ''} · {a.auteur?.nom ?? a.par_nom ?? '—'}
              </p>
              <p className="titre">{a.objet}</p>
              {a.notes && <p style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{a.notes}</p>}
              <div className="jetons" style={{ marginTop: 6 }}>
                {a.relance_le && (
                  <button className={`jeton ${!a.relance_faite && a.relance_le <= aujourdhui() ? 'chaud' : ''}`} onClick={() => void basculer(a)}>
                    {a.relance_faite ? '✓ Relance faite' : `Relancer le ${jourCalendrier(a.relance_le)} — marquer faite`}
                  </button>
                )}
                <button className="jeton" onClick={() => void retirer(a)}>
                  Retirer
                </button>
              </div>
            </div>
          )
        })}
        {lignes.length === 0 && !ajout && <p className="vide">Aucun échange noté.</p>}
      </div>
    </>
  )
}
